import {
  ChangeEmail,
  Company,
  CreditBoxReport,
  MNDAPayload,
  ReportType,
  SelectOption,
  SignUpV5Form,
  UpdateCapitalApplicationInformationPayload,
  UserIdentity,
} from '@hum/types';
import { createMutation } from '@hum/api/src/utils/createMutation';

export const createCreditBoxReports = createMutation<
  CreditBoxReport[],
  { companyId: string }
>(
  async ({ api, payload }) => {
    const reportTypes = Object.values(ReportType);
    const reportPromises = reportTypes.map(async (reportType) => {
      const report = await api.post(
        `/v2/companies/${payload.companyId}/reports/${reportType}`
      );
      return {
        id: report.id,
        companyId: report.companyId,
        createdAt: report.createdAt,
        type: report.reportType,
        status: report.status,
        rating: report.investorTestRating,
      };
    });

    const reports = await Promise.all(reportPromises);
    return reports;
  },
  {
    errorToast: {
      message: 'We couldn’t refresh the credit boxes.',
      options: {
        description: 'Please try again later.',
      },
    },
  }
);

export const createCompanyUser = createMutation<Partial<UserIdentity>>(
  ({ api, payload }) => {
    return api.post('/users/invite', payload);
  },
  {
    apiPrefix: 'identity',
    invalidates: ['/users'],
    successToast: { message: 'User invited successfully!' },
  }
);

export const updateCompanyUser = createMutation<Partial<UserIdentity>>(
  ({ api, payload }) => {
    return api.patch(`/users/${payload.id}`, {
      firstName: payload.firstName,
      lastName: payload.lastName,
      phone: payload.phone,
      position: payload.position,
    });
  },
  {
    apiPrefix: 'identity',
    invalidates: ['/users', '/contact'],
    successToast: { message: 'User updated successfully!' },
    errorToast: { message: 'Failed to update user' },
  }
);

export const updateCompanyUserStatus = createMutation<Partial<UserIdentity>>(
  ({ api, payload }) => {
    return api.patch(`/users/${payload.id}`, {
      status: payload.status,
    });
  },
  {
    apiPrefix: 'identity',
    invalidates: ['/users'],
    successToast: { message: 'User status updated successfully!' },
    errorToast: { message: 'Failed to update user status' },
  }
);

export const deleteCompanyUser = createMutation<Partial<UserIdentity>>(
  ({ api, payload }) => {
    return api.delete(`/users/${payload.id}`);
  },
  {
    apiPrefix: 'identity',
    invalidates: ['/users'],
    successToast: { message: 'User deleted successfully!' },
    errorToast: { message: 'Failed to delete user' },
  }
);

export const createCompany = createMutation<
  Company,
  {
    name: string;
    website: string;
    companyRole: string;
  }
>(
  ({ api, payload }) => {
    return api.post('/v2/companies', payload);
  },
  {
    invalidates: ['/companies'],
  }
);

export const acceptCompanyAgreements = createMutation<{
  organizationId: string;
}>(
  ({ api, payload }) => {
    return api.post(
      `/v2/companies/${payload.organizationId}/accept_agreements`,
      {}
    );
  },
  {
    invalidates: ['/companies'],
  }
);

export const createCommpanyFromAdmin = createMutation<SignUpV5Form>(
  ({ api, payload }) => {
    const { position, ...contact } = payload.contact || {};
    return api.post('/v5/admin_capital_applications', {
      contact: {
        ...contact,
        companyPosition: position,
      },
      company: {
        ...payload.company,
        website:
          payload?.company?.website?.startsWith('http://') ||
          payload?.company?.website?.startsWith('https://')
            ? payload?.company?.website.toLocaleLowerCase()
            : `https://${payload?.company?.website?.toLocaleLowerCase()}`,
      },
      application: {
        ...payload.application,
        lastRaised:
          payload.application?.lastRaised &&
          new Date(
            `${payload.application?.lastRaised
              .toString()
              .slice(
                0,
                2
              )}-01-${payload.application?.lastRaised.toString().slice(2)}`
          ).toISOString(),
        yearFounded: Number(payload.application?.yearFounded),
      },
    });
  },
  {
    successToast: { message: 'Company created successfully!' },
    errorToast: { message: 'An error occurred. Please try again.' },
  }
);

export const createMndaFile = createMutation<{ companyId: string }>(
  ({ api, payload }) => {
    return api.post(`/v5/companies/${payload.companyId}/mnda`);
  },
  {
    successToast: { message: 'MNDA signed!' },
    errorToast: { message: 'Failed to sign MNDA' },
  }
);

export const changeEmail = createMutation<{ message: string }, ChangeEmail>(
  ({ api, payload }) => {
    return api.post(`/users/change_email`, payload);
  },
  {
    errorToast: { message: 'Failed to change email' },
  }
);

export const acceptMNDA = createMutation<MNDAPayload>(
  ({ api, payload }) => {
    return api.post(`/companies/${payload.companyId}/onboarding/mnda`, {
      firstName: payload.firstName,
      lastName: payload.lastName,
      role: payload.role,
    });
  },
  {
    invalidates: ['/companies'],
    successToast: {
      message: 'We’ve emailed you a signed copy of the agreement',
    },
    errorToast: { message: 'Failed to accept MNDA' },
  }
);

export const editCompanyFormClosed = createMutation<{
  companyId: number | undefined;
  syndicationInvestor: any;
  company: any;
}>(
  ({ api, payload }) => {
    return api.patch(`/companies/${payload.companyId}/syndication/investors`, {
      syndicationInvestor: payload.syndicationInvestor,
      company: payload.company,
    });
  },
  {
    successToast: { message: 'Updated syndication investor successfully!' },
    errorToast: {
      message: 'Failed to update syndication investor information.',
    },
  }
);

export const resendInvite = createMutation<{
  email: string;
  isInvite: boolean;
}>(
  ({ api, payload }) => {
    return api.post(`/reset-password-email`, {
      email: payload.email,
      isInvite: payload.isInvite,
    });
  },
  {
    apiPrefix: 'identity',
    successToast: { message: 'Invite resent' },
    errorToast: { message: 'Failed to resend invite' },
  }
);

export const serviceStartDataPull = createMutation<{
  companyId: number;
  connectorId: number;
}>(
  ({ api, payload }) => {
    return api.post(
      `/companies/${payload.companyId}/connectors/${payload.connectorId}/connector_task`,
      { is_test: false }
    );
  },
  {
    successToast: { message: 'Data pull started!' },
    errorToast: { message: 'Failed to start data pull' },
  }
);

export const serviceStartDataPullAirbyte = createMutation<{
  companyId: number;
  connectorId: number;
}>(
  ({ api, payload }) => {
    return api.post(
      `/companies/${payload.companyId}/airbyte/connectors/${payload.connectorId}/connector_task`,
      { is_test: false }
    );
  },
  {
    successToast: { message: 'Data pull started!' },
    errorToast: { message: 'Failed to start data pull' },
  }
);

export const createPrincipalInvestment = createMutation<{ companyId: string }>(
  ({ api, payload }) => {
    return api.post(`/v2/companies/${payload.companyId}/principal-investments`);
  },
  {
    invalidates: ['/principal-investments'],
    successToast: { message: 'Principal investment created!' },
    errorToast: { message: 'Failed to create principal investment' },
  }
);

export const patchUnwantedInvestors = createMutation<{
  unwantedInvestors: SelectOption[];
  companyId: string;
}>(
  ({ api, payload }) => {
    return api.patch(`/v2/companies/${payload.companyId}`, {
      capitalApplication: {
        unwantedInvestors: payload.unwantedInvestors?.map(
          (investor) => investor.value
        ),
      },
    });
  },
  {
    successToast: { message: 'Unwanted investor saved' },
    errorToast: { message: 'Failed to save unwanted investor' },
  }
);

export const updateCapitalApplication = createMutation<
  UpdateCapitalApplicationInformationPayload & { companyId: string }
>(
  ({ api, payload }) => {
    return api.patch(`/v2/companies/${payload.companyId}`, {
      capitalApplication: {
        yearFounded: payload.application?.yearFounded,
        selfReportedIndustry: payload.application?.selfReportedIndustry,
        revenueModel: payload.application?.revenueModel,
        investmentAmount: payload.application?.investmentAmount,
        fundraisingTimeFrame: payload.application?.fundraisingTimeFrame,
        useOfFunds: payload.application?.useOfFunds,
        capitalDeployment: payload.application?.capitalDeployment,
        lastFundingRound: payload.application?.lastFundingRound,
        equityRaised: payload.application?.totalEquityRaised,
        latestPostMoneyValuation: payload.application?.latestPostMoneyValuation,
        yearlyRevenue: payload.application?.yearlyRevenue,
        monthsOfRunway: payload.application?.monthsOfRunway,
        recordSystem: payload.application?.recordSystem,
        paymentProcessor: payload.application?.paymentProcessor,
      },
    });
  },
  {
    invalidates: ['/companies'],
    successToast: { message: 'Updated Capital Application successfully!' },
    errorToast: { message: 'Failed to update Capital Application' },
  }
);

export const updateCompanyTestAccount = createMutation<{
  companyId: string;
  isTestAccount: boolean;
}>(
  ({ api, payload }) => {
    return api.patch(`/v2/companies/${payload.companyId}`, {
      capitalApplication: {
        test: payload.isTestAccount,
      },
    });
  },
  {
    invalidates: ['/companies'],
    successToast: { message: 'Company test account updated successfully' },
    errorToast: { message: 'Failed to update test account' },
  }
);

export const updateCompany = createMutation<Company>(
  ({ api, payload }) => {
    const { uuid, application, ...rest } = payload;
    return api.patch(`/v2/companies/${uuid}`, {
      ...rest,
      capitalApplication: {
        ...application,
      },
    });
  },
  {
    invalidates: ['/companies'],
    successToast: { message: 'Updated general information successfully' },
    errorToast: { message: 'Failed to update company information.' },
  }
);

export const updateTags = createMutation<{
  companyId: string;
  tags: SelectOption[];
}>(
  ({ api, payload }) => {
    return api.patch(`/v2/companies/${payload.companyId}/tags`, {
      tags: payload.tags?.map((tag) => tag.value) || [],
    });
  },
  {
    invalidates: ['/companies'],
    successToast: { message: 'Tags updated successfully' },
    errorToast: { message: 'Failed to update tags' },
  }
);

export const downloadCreditBoxReport = createMutation<
  { url: string },
  {
    companyId: string;
    creditBoxReportId: string;
  }
>(
  ({ api, payload }) => {
    return api.post(
      `/v2/companies/${payload.companyId}/reports/${payload.creditBoxReportId}/download_url`
    );
  },
  {
    errorToast: {
      message: 'We couldn’t download the report.',
      options: {
        description: 'Please try again later.',
      },
    },
  }
);

export const startCompanyAnalysis = createMutation<{
  companyId: string;
}>(({ api, payload }) => {
  return api.post(`/v2/companies/${payload.companyId}/company_analysis`);
});
